<template>
  <div class="booking-term" :class="{ 'booking-term--card': isCardMode }">
    <div v-if="isCardMode" class="booking-term-cards">
      <div v-if="loadingCards" class="small text-muted">
        carregando assinaturas...
      </div>
      <div v-else-if="!signedClients.length" class="small text-muted">
        Nenhuma assinatura registrada
      </div>
      <div v-else>
        <div class="text-muted">Assinado por:</div>
        <div
          v-for="(client, index) in signedClients"
          :key="client.key || `${client.doc}-${index}`"
          class="booking-term-signature-card mb-2"
        >
          <div class="booking-term-signature-card-name">
            {{ abbreviateName(client.name) }}
          </div>
          <div class="booking-term-signature-card-doc">
            {{ formatCpf(client.doc, true) }}
          </div>
          <div class="booking-term-signature-card-date">
            {{ formatSignatureDate(client) }}
          </div>
        </div>
      </div>
    </div>

    <template v-else>
      <div v-if="showHeader" class="booking-term-header">
        <h2 class="border-bottom w-100 text-left pb-0">
          <button
            @click.prevent="close"
            type="button"
            class="close"
            aria-label="Close"
          >
            <span>x</span>
          </button>
          <span>&nbsp;</span>
        </h2>
      </div>

      <div
        class="booking-term-content pt-0 bp-0"
        id="booking-term-content"
        tabindex="0"
      >
        <div v-html="formattedJanooTerms"></div>
        <div v-if="formattedJanooTerms == ''" class="text-center">
          <span class="mx-3">carregando...</span>
        </div>
        <div
          v-if="
            clients.length &&
            formattedJanooTerms !== '' &&
            showSignFooter &&
            !isQuotationBooking
          "
          id="booking-term-signatures"
        >
          <p style="text-align: center">
            <strong
              ><span style="text-transform: uppercase">Assinaturas</span>
            </strong>
          </p>

          <div
            v-for="(client, index) in clients"
            :key="client.key"
            class="booking-term-client-group mb-4"
          >
            <div
              class="booking-term-client-header d-flex align-items-start justify-content-between"
              role="button"
              @click="toggleClientGroup(index)"
            >
              <div class="mb-0 pr-2">
                <strong>{{ client.item_label }}</strong>
                <span v-if="client.item_label_description" class="text-muted">
                  — {{ client.item_label_description }}
                </span>
                <div class="small text-muted">
                  Pessoa {{ client.person_index }} de {{ client.item_quantity }}
                </div>
                <div
                  v-if="isClientLocked(client) && !client.expanded"
                  class="small text-success font-weight-bold mt-1"
                >
                  <span v-if="hasUnsignedClients"
                    >Assinatura já registrada</span
                  >
                </div>
                <div v-else-if="!isClientLocked(client)">
                  <span
                    v-if="isClientFormComplete(client)"
                    class="small text-success mt-1"
                  >
                    dados válidos para assinatura
                  </span>
                  <span v-else class="small text-danger mt-1">
                    Assinatura pendente
                  </span>
                </div>
              </div>

              <button
                v-if="hasUnsignedClients"
                type="button"
                class="booking-term-collapse-btn btn btn-link p-0"
                :aria-expanded="client.expanded ? 'true' : 'false'"
                :aria-label="
                  client.expanded ? 'Fechar formulário' : 'Abrir formulário'
                "
                @click.stop="toggleClientGroup(index)"
              >
                <i
                  :class="`no ${
                    client.expanded ? 'no-up-arrow' : 'no-down-arrow'
                  }`"
                  style="padding: 4px; margin: 2px"
                  type="button"
                ></i>
              </button>
            </div>

            <div
              v-show="client.expanded"
              class="booking-term-client-fields mt-2"
            >
              <b-row>
                <b-col cols="12" md="6" class="mb-2">
                  <label class="small mb-1">Nome</label>
                  <b-form-input
                    v-model="client.name"
                    placeholder="Nome completo"
                    :disabled="isClientLocked(client)"
                    :class="{
                      'is-invalid':
                        !isValidName(client.name) && !isClientLocked(client),
                    }"
                  />
                </b-col>

                <b-col cols="12" md="6" class="mb-2">
                  <label class="small mb-1">Documento</label>
                  <b-form-input
                    v-model="client.doc"
                    :class="{
                      'is-invalid':
                        !isValidDoc(client.doc) && !isClientLocked(client),
                    }"
                    maxlength="18"
                    v-mask="getDocMask(client)"
                    placeholder="CPF, CNPJ ou Passaporte"
                    :disabled="isClientLocked(client)"
                    @keyup="client.doc = String(client.doc || '').toUpperCase()"
                  />
                </b-col>

                <b-col cols="12" md="6" class="mb-2">
                  <label class="small mb-1">Data de nascimento</label>
                  <b-form-input
                    v-model="client.birthday"
                    type="date"
                    :class="{
                      'is-invalid':
                        !isValidDate(client.birthday) &&
                        !isClientLocked(client),
                    }"
                    :disabled="isClientLocked(client)"
                  />
                </b-col>

                <b-col cols="12" md="6" class="mb-2">
                  <label class="small mb-1">Telefone</label>
                  <b-form-input
                    v-model="client.phone"
                    v-mask="'(##) #####-####'"
                    placeholder="(00) 00000-0000"
                    :class="{
                      'is-invalid':
                        !isValidPhone(client.phone) && !isClientLocked(client),
                    }"
                    :disabled="isClientLocked(client)"
                  />
                </b-col>
              </b-row>

              <div
                v-if="isClientLocked(client)"
                class="small text-success mt-1"
              >
                <span v-if="hasUnsignedClients">Assinatura já registrada</span>
              </div>
              <div
                v-else-if="!isClientLocked(client) && !client.expanded"
                class="small text-danger mt-1"
              >
                Assinatura pendente
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="showFooter"
        class="booking-term-footer border-top w-100 pt-2 pb-3"
      >
        <b-row style="padding-bottom: 10px">
          <b-col class="text-right pb-0">
            <!-- <b-button
              @click="close"
              variant="outline-danger"
              class="rounded-pill"
            >
              Fechar
            </b-button> -->
            <b-button
              id="sign-button"
              v-if="showSignButton && termIsScrolled && hasUnsignedClients"
              :disabled="disabledSignButton"
              @click="sign"
              variant="success"
              class="rounded-pill"
            >
              {{ showSignFooter ? 'Assinar ' : 'Aceitar' }}
            </b-button>
            <b-button
              v-if="hasUnsignedClients"
              id="scroll-button"
              @click="scrollToSignButton"
              :disabled="termIsScrolled || !signaturesLoaded"
              variant="primary"
              class="rounded-pill"
            >
              Prosseguir
            </b-button>
            <b-button
              variant="primary"
              class="rounded-pill"
              :disabled="true"
              v-if="!signaturesLoaded"
            >
              ...aguarde
            </b-button>
          </b-col>
        </b-row>
      </div>
    </template>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import moment from 'moment'
import swal from 'sweetalert2'
import { VueMaskDirective } from 'v-mask'
import nodeService from '@/services/nodejs'
import { v4 as uuidv4 } from 'uuid'
import productXFields from '@/views/Products/store/product-xfields.json'
import { strNormalize } from '@/store/utils/general'

export default {
  name: 'BookingTerm',

  directives: { mask: VueMaskDirective },

  props: {
    showHeader: {
      type: Boolean,
      default: true,
    },
    showFooter: {
      type: Boolean,
      default: true,
    },
    enableSignatureFooter: {
      type: Boolean,
      default: false,
    },
    booking_code: {
      type: String,
      default: '',
    },
    place_uuid: {
      type: String,
      default: '',
    },
    product_uuid: {
      type: String,
      default: '',
    },
    hash: {
      type: String,
      default: '',
    },
    items: {
      type: Array,
      default: function () {
        return []
      },
    },
    mode: {
      type: String,
      default: '',
    },
    termDate: {
      type: String,
      default: '',
    },
  },

  computed: {
    termIsScrolled() {
      return this.signatureSessionIndex > this.signatureSessions.length - 1
    },

    hasUnsignedClients() {
      return this.clients.filter((client) => client.signed == false).length > 0
    },

    hasCommitmentTerms() {
      return this.formattedJanooTerms !== ''
    },

    /**
     * Há tickets com identidade obrigatória e os clientes ainda não
     * passaram na validação necessária para assinar.
     */
    hasPendingRequiredClientSignatures() {
      this.duplicatedClientMsg = ''
      if (this.isQuotationBooking) return false

      let signedClients = this.clients || []
      signedClients = signedClients.filter((client) => client.signed)
      if (!this.booking_code) {
        return (
          !this.allSignaturesValid() ||
          this.validatedClients(true) != this.validatedClients()
        )
      } else {
        return (
          this.validatedClients(true) == 0 ||
          this.validatedClients(true) != this.validatedClients()
        )
      }
    },

    disabledSignButton() {
      return (
        !this.termIsScrolled &&
        (!this.hasCommitmentTerms || this.hasPendingRequiredClientSignatures)
      )
    },

    /**
     * Reserva confirmada (não cotação): o botão aparece se ainda não há
     * código da reserva ou se ainda existem clientes sem assinar.
     */
    canShowSignButtonForConfirmedBooking() {
      if (this.isQuotationBooking) return false
      return this.booking_code != '' && this.hasUnsignedClients
    },

    /**
     * Cotação: o botão só aparece antes de existir booking_code, com
     * termos carregados e sem pendência de assinatura obrigatória.
     */
    canShowSignButtonForQuotation() {
      if (!this.isQuotationBooking) return false
      if (this.booking_code) return false
      if (!this.hasCommitmentTerms) return false
      if (this.hasPendingRequiredClientSignatures) return false
      return true
    },
    canShowSignButtonForBookingExpress() {
      return true
    },
    showSignButton() {
      const show =
        this.canShowSignButtonForConfirmedBooking ||
        this.canShowSignButtonForQuotation ||
        this.canShowSignButtonForBookingExpress
      return show && this.showSignFooter
    },

    isQuotationBooking() {
      const isApproved =
        this.bookingDetails &&
        this.bookingDetails.length > 0 &&
        this.bookingDetails[0].payload &&
        this.bookingDetails[0].payload.term &&
        this.bookingDetails.filter(
          (booking) => booking.bookingCode == this.booking_code
        ).length > 0

      return (
        this.form &&
        !isApproved &&
        this.form.cod_booking == false &&
        this.form.payLater == true
      )
    },
    ...mapGetters('booking', {
      booking_terms_conditions: 'getTermsConditions',
      form: 'getReservaForm',
    }),

    ...mapGetters('operacao', {
      bookingDetails: 'getBookingDetails',
    }),
    ...mapGetters('product', {
      poisList: 'getPois',
    }),

    bookingRequiredTickets() {
      if (
        (this.poisList && this.poisList.length == 0) ||
        this.isQuotationBooking
      ) {
        return []
      }
      const poiIndex = this.poisList.findIndex(
        (p) => p.uuid == this.form.atrativo.uuid
      )

      const valid_tickets = this.form.tickets.filter(
        (ticket) => ticket.quantity > 0
      )

      const identity_required_tickets = this.poisList[poiIndex]
        ? Object.values(this.poisList[poiIndex].tickets).filter(
            (ticket) =>
              ticket.identity_required &&
              valid_tickets.find((t) => t.id == ticket.id)
          )
        : []
      return identity_required_tickets
    },

    isCardMode() {
      return this.mode === 'card'
    },

    signedClients() {
      return this.clients.filter((client) => client.signed !== false)
    },

    formattedJanooTerms() {
      const termDebug = false

      //load place terms (janoo and place terms)
      if (!this.product || !this.janooTerms || !this.vendorTerms) {
        return ''
      }

      var selected_items = ''


      var termText = ''
      const termSessionIds = []
      const termBlocks = []

      termBlocks.push(this.janooTerms)
      let productTerms = ''
      productTerms +=
        'REGRAS DE VISITAÇÃO - ${product_place_name} - ${product_name}<br><br> É um grande prazer em receber visitantes e compartilhar as belezas desse lugar. No entanto, precisamos orientar os visitantes sobre os riscos e responsabilidades ao visitar uma área como essa e expor algumas características das nossas atrações.<br><br>'
      this.productTermXFieldKeys().forEach((key) => {
        let xfieldValue = this.resolveProductXField(key)

        let parsedXfieldValue = ''
        let parsedXfieldLabel = productXFields[key]['public_label']
        parsedXfieldLabel = parsedXfieldLabel.split('#')
        parsedXfieldLabel = parsedXfieldLabel[1] || parsedXfieldLabel[0]
        if (
          parsedXfieldLabel.endsWith('?') ||
          parsedXfieldLabel.endsWith(':')
        ) {
          parsedXfieldLabel = parsedXfieldLabel.slice(0, -1)
        }
        if (
          Object.prototype.toString.call(xfieldValue) == '[object Array]' &&
          xfieldValue.length > 0
        ) {
          parsedXfieldValue = `<br><ul>
            ${xfieldValue.map((item) => `<li>${item}</li>`).join('')}
          </ul>`
        } else {
          if (this.resolveProductXField(key)) {
            parsedXfieldValue = ` ${this.resolveProductXField(key)}<br><br>`
          }
        }
        if (parsedXfieldValue && parsedXfieldValue != 'null') {
          productTerms += `${parsedXfieldLabel}:${parsedXfieldValue}`
        }
      })
      termBlocks.push(productTerms)
      termBlocks.push(this.vendorTerms)

      //apply place and product details to termBlocks
      /**
       * 
            'Gestor: [Gestor]
            'Email do gestor: [Email do gestor]
            'Telefone do gestor: [Telefone do gestor]
            'Endereço do gestor: [Endereço do gestor]
            'Hoje: [Hoje]
            'Agora: [Agora]
            'Nome: [Nome]
            'Documento: [Documento]
            'Local: [Local]
            'Atrativo: [Atrativo]
            'Quantidade: [Quantidade]
            'Total: [Total]
            'Códgo da Reserva: [Códgo da Reserva]
            'Termo: [Termo]
            'Data do Termo: [Data do Termo]
            'Cliente: [Cliente]
            'Email: [Email]
            'Telefone: [Telefone]
            'Cliente: [Cliente]
            'Telefone: [Telefone]
            'Data de Nascimento: [Data de Nascimento]
            'Email: [Email]
            'Cidade: [Cidade]
            'Estado: [Estado]
            'CEP: [CEP]<br>.........................
            'Gestor: [Gestor]
            'Email do gestor: [Email do gestor]
            'Telefone do gestor: [Telefone do gestor]
            'Endereço do gestor: [Endereço do gestor]
            'Hoje: [Hoje]

            DATA HORA
            'Agora: [Agora]

            CLIENTE
            'Nome: [Nome]
            'Documento: [Documento]

            ATRATIVO
            'Local: [Local] product_place_name
            'Atrativo: [Atrativo] product_name

            RESERVA
            'Quantidade: [Quantidade] tickets.quantity
            'Total: [Total] valor final
            'Códgo da Reserva: [Códgo da Reserva]
            'Termo: [Termo]
            'Data do Termo: [Data do Termo]
            'Cliente: [Cliente]
            'Email: [Email]
            'Telefone: [Telefone]
            'Cliente: [Cliente]
            'Telefone: [Telefone]
            'Data de Nascimento: [Data de Nascimento]
            'Email: [Email]
            'Cidade: [Cidade]
            'Estado: [Estado]
            'CEP: [CEP]
       */
      termBlocks.forEach((term, index) => {
        if (!term || typeof term !== 'string') return
        if (termDebug && index == 0) {
          term +=
            'Gestor: [Gestor]<br>' +
            'Email do gestor: [Email do gestor]<br>' +
            'Telefone do gestor: [Telefone do gestor]<br>' +
            'Endereço do gestor: [Endereço do gestor]<br>' +
            'Hoje: [Hoje]<br>' +
            'Agora: [Agora]<br>' +
            'Nome: [Nome]<br>' +
            'Documento: [Documento]<br>' +
            'Local: [Local]<br>' +
            'Atrativo: [Atrativo]<br>' +
            'Quantidade: [Quantidade]<br>' +
            'Total: [Total]<br>' +
            'Códgo da Reserva: [Códgo da Reserva]<br>' +
            'Termo: [Termo]<br>' +
            'Data do Termo: [Data do Termo]<br>' +
            'Cliente: [Cliente]<br>' +
            'Email: [Email]<br>' +
            'Telefone: [Telefone]<br>' +
            'Cliente: [Cliente]<br>' +
            'Telefone: [Telefone]<br>' +
            'Data de Nascimento: [Data de Nascimento]<br>' +
            'Email: [Email]<br>' +
            'Cidade: [Cidade]<br>' +
            'Estado: [Estado]<br>' +
            'CEP: [CEP]<br>.........................<br>'
        }
        term = term.replaceAll(
          '${product_place_name}',
          this.product.product_place_name || ''
        )
        term = term.replaceAll(
          '${product_name}',
          this.product.product_name || ''
        )
        term = term.replaceAll(
          '${vendor_name}',
          this.product.vendor_name || 'gestor'
        )
        term = this.applyProductXFieldsToTerm(term)
        /*
        if (
          termDebug &&
          this.product.commitment_terms &&
          this.product.commitment_terms.length > 0
        ) {
          selected_items =
            '<strong>[DEBUG - commitment term items]</strong><br>' +
            selected_items
        }

        if (term.indexOf('${commitment_terms_text}') > -1) {
          if (this.product.commitment_terms_text) {
            term += '<div id="commitment-terms-text"></div>'
          } else {
            this.signatureSessions = this.signatureSessions.filter(
              (session) => session != 'commitment-terms-text'
            )
          }
          term = term.replaceAll(
            '${commitment_terms_text}',
            this.product.commitment_terms_text || ''
          )
        }
        if (term.indexOf('${commitment_terms}') > -1) {
          term = term.replaceAll('${commitment_terms}', selected_items)
        }
        */

        const address = (this.form && this.form.address) || {}
        const customer = this.customer || {}
        term = term.replaceAll('[Cidade]', address.city || '')
        term = term.replaceAll('[Estado]', address.state || '')
        term = term.replaceAll('[CEP]', address.zipcode || '')
        term = term.replaceAll('[Hoje]', moment().format('DD/MM/YYYY'))
        term = term.replaceAll(
          '[Agora]',
          moment().format('DD/MM/YYYY, HH:mm:ss')
        )
        term = term.replaceAll('[Nome]', customer.name || '')
        term = term.replaceAll(
          '[Documento]',
          this.formatDoc(customer.doc || '')
        )
        term = term.replaceAll('[Local]', this.product.product_place_name || '')
        term = term.replaceAll(
          '[Atrativo]',
          this.formatDoc(this.product.product_name || '')
        )
        term = term.replaceAll(
          '[Quantidade]',
          this.items.reduce((acc, item) => acc + item.quantity, 0)
        )
        term = term.replaceAll(
          '[Total]',
          this.items.reduce((acc, item) => acc + item.quantity * item.price, 0)
        )

        termText +=
          '<div id="term-session-' +
          index +
          '" class="term-session">' +
          this.filterTermSessionsBySelectedTickets(term) +
          '</div>'
        if (index > 0) {
          const sessionId = 'term-session-' + index
          if (this.signatureSessions.indexOf(sessionId) == -1) {
            termSessionIds.push(sessionId)
          }
        }

        if (this.bookingDetails && this.bookingDetails.length > 0) {
          termText = termText.replaceAll(
            '[Códgo da Reserva]',
            this.bookingDetails[0].payload.booking_code
          )
          termText = termText.replaceAll(
            '[Data da Reserva]',
            moment(this.bookingDetails[0].payload.booking_date).format(
              'DD/MM/YYYY'
            )
          )
          termText = termText.replaceAll(
            '[Hora da Reserva]',
            moment(this.bookingDetails[0].payload.booking_date).format(
              'HH[h]mm'
            )
          )
          termText = termText.replaceAll(
            '[Termo]',
            this.bookingDetails[0].payload.term
              ? this.bookingDetails[0].payload.term.hash
              : ''
          )
          termText = termText.replaceAll(
            '[Data do Termo]',
            this.bookingDetails[0].payload.term
              ? this.bookingDetails[0].payload.term.datetime
              : ''
          )
          const address = (this.form && this.form.address) || {}
          const customer = this.customer || {}
          const vendor =
            (this.bookingDetails[0].payload &&
              this.bookingDetails[0].payload.vendor) ||
            {}
          termText = termText.replaceAll('[Cliente]', customer.name || '')
          termText = termText.replaceAll('[Email]', customer.email || '')
          termText = termText.replaceAll('[Telefone]', customer.phone || '')
          termText = termText.replaceAll('[Gestor]', vendor.store_name || '')
          termText = termText.replaceAll(
            '[Email do gestor]',
            vendor.email || ''
          )
          termText = termText.replaceAll(
            '[Telefone do gestor]',
            vendor.phone || ''
          )
          termText = termText.replaceAll(
            '[Endereço do gestor]',
            [
              address.street,
              address.number,
              [address.city, address.state].filter(Boolean).join('-'),
              address.country,
            ]
              .filter(Boolean)
              .join(', ') +
              '. ' +
              [address.complement, address.neighborhood, address.zipcode]
                .filter(Boolean)
                .join(' ')
          )
        }
        termText = termText.replaceAll('[Cliente]', customer.name || '')

        termText = termText.replaceAll('[Telefone]', customer.phone || '')
        termText = termText.replaceAll(
          '[Data de Nascimento]',
          customer.birthday || ''
        )
      })
      if (termSessionIds.length) {
        this.signatureSessions = termSessionIds.concat(this.signatureSessions)
      }
      if (this.bookingRequiredTickets.length > 0) {
        this.signatureSessions.push('booking-term-signatures')
      }
      return termText
    },
  },

  data() {
    return {
      janooTerms: '',
      vendorTerms: '',
      duplicatedClientMsg: '',
      signatureSessions: [],
      signatureSessionIndex: 0,
      product: null,
      customer: null,
      clients: [],
      placeUuid: null,
      productUuid: null,
      term_hash: null,
      booking: null,
      showSignFooter: false,
      deviceContext: null,
      locationContext: null,
      loadingCards: false,
      signaturesLoaded: true,
    }
  },

  methods: {
    scrollToSignButton() {
      const container = document.getElementById('booking-term-content')
      const sessionId = this.signatureSessions[this.signatureSessionIndex]
      console.log(
        'sessionId',
        sessionId,
        this.signatureSessionIndex,
        this.signatureSessions
      )
      const target = sessionId ? document.getElementById(sessionId) : null
      if (!container || !target) {
        this.signatureSessionIndex++

        return
      }

      let extraScroll = 0
      if (this.signatureSessionIndex == this.signatureSessions.length - 1) {
        extraScroll = 420
      }

      const top =
        target.getBoundingClientRect().top -
        container.getBoundingClientRect().top +
        container.scrollTop +
        extraScroll
      container.scrollTo({
        top,
        behavior: 'smooth',
      })
      setTimeout(() => {
        if (this.signatureSessionIndex < this.signatureSessions.length) {
          this.signatureSessionIndex++
        }
      }, 500)
    },
    ...mapActions('operacao', ['fetchBookingByUUID', 'resetBookingDetails']),
    ...mapActions('booking', ['fetchTermsConditions']),
    ...mapActions('product', ['getPOIByUUID', 'fetchPOIs']),
    async loadPois() {
      try {
        const list = await this.getPOIByUUID({
          PLACE_UUID: this.placeUuid || '',
          PRODUCT_UUID: this.productUuid || '',
        })
        const poi = (list && list[0]) || null
        if (!poi) {
          swal.fire({
            title: 'Erro',
            text: 'Não foi possível encontrar o produto',
            icon: 'error',
          })
        }
        this.product = poi
      } catch (error) {
        this.product = null
      }
    },

    async loadTerms() {
      try {
        this.janooTerms = await this.fetchTermsConditions()

        this.vendorTerms = await this.fetchTermsConditions(
          this.product.product_place
        )
      } catch (error) {
        return
      }
    },
    close() {
      this.$emit('close')
    },

    productTermXFieldKeys() {
      return [
        'guidance_items',
        'guidance_list',
        'includes',
        'not_included',
        'activity_rules_info',
        'activity_recomendations',
        'activity_rules',
        'security_warning',
        'commitment_terms_text',
        'commitment_terms',
      ]
    },

    resolveProductXField(key) {
      if (!this.product || !key) return null

      const unwrap = (raw) => {
        if (raw == null || raw === '') return null
        if (Array.isArray(raw)) return raw
        if (typeof raw === 'object' && raw.value !== undefined) {
          return raw.value
        }
        return raw
      }

      const candidates = [
        this.product[key],
        this.product.xfields && this.product.xfields[key],
        this.product.xfields &&
          this.product.xfields.laravel_data &&
          this.product.xfields.laravel_data[key],
      ]

      for (let i = 0; i < candidates.length; i++) {
        const value = unwrap(candidates[i])
        if (value != null && value !== '') return value
      }
      return null
    },

    formatProductXFieldValue(value) {
      if (value == null || value === '') return ''

      if (typeof value === 'string') {
        const trimmed = value.trim()
        if (!trimmed || trimmed.toUpperCase() === 'NÃO INFORMADO') return ''
        if (
          (trimmed.charAt(0) === '[' &&
            trimmed.charAt(trimmed.length - 1) === ']') ||
          (trimmed.charAt(0) === '{' &&
            trimmed.charAt(trimmed.length - 1) === '}')
        ) {
          try {
            return this.formatProductXFieldValue(JSON.parse(trimmed))
          } catch (error) {}
        }
        return trimmed.replace(/\n/g, '<br>')
      }

      if (Array.isArray(value)) {
        const items = value
          .map((item) => {
            if (item == null) return ''
            if (typeof item === 'object') {
              return (
                item.info ||
                item.title ||
                item.label ||
                item.value ||
                item.text ||
                ''
              )
            }
            return String(item)
          })
          .map((item) => String(item).trim())
          .filter((item) => item && item.toUpperCase() !== 'NÃO INFORMADO')
        if (!items.length) return ''
        return `<ul>${items.map((item) => `<li>${item}</li>`).join('')}</ul>`
      }

      if (typeof value === 'object') {
        const objectText =
          value.info || value.title || value.label || value.text || ''
        if (objectText) {
          return this.formatProductXFieldValue(objectText)
        }
        const selected = Object.keys(value).filter((key) => {
          if (
            ['type', 'label', 'public_label', 'options', 'id'].indexOf(key) >= 0
          ) {
            return false
          }
          return !!value[key]
        })
        if (selected.length) {
          return this.formatProductXFieldValue(selected)
        }
        return ''
      }

      return String(value)
    },

    applyProductXFieldsToTerm(term) {
      if (!term || typeof term !== 'string') return term
      this.productTermXFieldKeys().forEach((key, index) => {
        const token = '${' + key + '}'
        if (term.indexOf(token) === -1) return
        term = term.replaceAll(
          token,
          '<div id="' +
            key +
            '">' +
            this.formatProductXFieldValue(this.resolveProductXField(key)) +
            '</div>'
        )
      })
      return term
    },

    normalizeTicketId(id) {
      const digits = String(id || '').replace(/\D/g, '')
      if (!digits) return ''
      return String(Number(digits))
    },

    getSelectedTicketIds() {
      let items =
        this.items && this.items.length
          ? this.items
          : this.booking && this.booking.payload && this.booking.payload.items
          ? this.booking.payload.items
          : []

      items = items.filter((item) => Number(item.quantity) > 0)
      const ids = new Set()
      items.forEach((item) => {
        if (Number(item.quantity) >= 1) {
          const normalized = this.normalizeTicketId(item.id)
          if (normalized) ids.add(normalized)
        }
      })
      return ids
    },

    filterTermSessionsBySelectedTickets(termHtml) {
      if (!termHtml) return ''

      const sessionRegex = /#(\d+)/g
      const matches = []
      let match
      while ((match = sessionRegex.exec(termHtml)) !== null) {
        let sessionTitleStart = termHtml.lastIndexOf('<', match.index)
        matches.push({
          id: match[1],
          index: sessionTitleStart,
        })
      }

      if (!matches.length) return termHtml

      const allowedIds = this.getSelectedTicketIds()
      let filtered = termHtml.slice(0, matches[0].index)
      matches.forEach((session, index) => {
        const end =
          index + 1 < matches.length
            ? matches[index + 1].index
            : termHtml.length
        let sessionHtml = termHtml.slice(session.index, end)
        const sessionId = this.normalizeTicketId(session.id)
        if (sessionId && allowedIds.has(sessionId)) {
          const item = this.form.tickets
            .filter((ticket) => Number(ticket.quantity) > 0)
            .find((item) => item.id === session.id)
          sessionHtml = sessionHtml.replace(
            '#' + session.id,
            '<hr><p style="text-align: center;"><strong><span style="text-transform: uppercase;">' +
              this.product.vendor_name +
              ' - ' +
              this.product.product_place_name +
              ' - ' +
              (item ? item.label : '') +
              '</span>' +
              '</strong></p>'
          )
          filtered += sessionHtml
        }
      })
      return filtered
    },
    allSignaturesValid() {
      let validSignareList = []
      let newSignareList = []
      this.clients.forEach((client) => {
        if (this.isClientFormComplete(client) || client.signed == true) {
          newSignareList.push(client)
        }
        if (
          (this.isClientFormComplete(client) && client.signed == false) ||
          client.signed == true
        ) {
          validSignareList.push(client)
        }
      })
      return validSignareList.length == this.clients.length
    },
    validatedClients(checkUnique = false) {
      /**
       * checkUnique: true -> verifica se o cliente já assinou o termo
       */
      let hasValidClients = false
      let validSignareList = []
      let duplicatedSignareList = []
      this.clients.forEach((client) => {
        if (this.isClientFormComplete(client) && client.signed == false) {
          hasValidClients = true
          validSignareList.push(client)
        }
      })
      if (checkUnique) {
        validSignareList.forEach((client) => {
          this.clients.forEach((c) => {
            if (
              c.key != client.key &&
              (c.doc == this.formatCpf(client.doc) ||
                c.doc == this.formatCpf(client.doc, true))
            ) {
              duplicatedSignareList.push(JSON.parse(JSON.stringify(client)))
            }
          })
        })
      }
      if (duplicatedSignareList.length > 0) {
        this.duplicatedClientMsg = duplicatedSignareList.reduce(
          (acc, duplicated) =>
            acc + 'Cliente <b>' + duplicated.name + '</b> duplicado.<br>',
          ''
        )
      }
      return validSignareList.length - duplicatedSignareList.length
    },
    async sign() {
      if (this.hasPendingRequiredClientSignatures) {
        let msg = ''
        this.bookingRequiredTickets.forEach((ticket) => {
          if (
            this.clients.find(
              (c) => c.item_id == ticket.id && c.signed == false
            )
          ) {
            msg +=
              this.duplicatedClientMsg ||
              'Identificação obrigatória para ' + ticket.label + '.<br>'
          }
        })
        if (msg == '') {
          let signedClients = this.clients.filter((client) => client.signed)

          if (
            signedClients.length != this.clients.length &&
            this.termIsScrolled
          ) {
            this.$notify({
              message:
                this.duplicatedClientMsg ||
                'Preencha os dados de ao menos 1 assinatura',
              duration: 4000,
              dangerouslyUseHTMLString: true,
              type: 'warning',
            })
          }
        }
        if (msg != '') {
          this.$notify({
            message: msg,
            duration: 4000,
            dangerouslyUseHTMLString: true,
            type: 'warning',
          })
        }
        this.signaturesLoaded = true
        return
      }
      const hash =
        this.term_hash ||
        this.hash ||
        (this.booking &&
          this.booking.payload &&
          this.booking.payload.term &&
          this.booking.payload.term.hash) ||
        ''

      const isFirstSign =
        this.clients.filter((client) => client.doc.indexOf('***') > 0).length ==
        0
      const isLastSign =
        this.clients.filter(
          (client) => client.signed == true || this.isClientFormComplete(client)
        ).length == this.clients.length
      const datetime = new Date().toISOString()
      const payload = {
        hash,
        clients: this.buildTermSignatureClients(datetime),
        datetime,
        uuid_ref: hash,
        booking_code: this.booking ? this.booking.bookingCode : '',
      }

      // Na primeira assinatura, envia o HTML do termo sem as assinaturas.
      // Cada cliente que assina leva o próprio sign_html; o backend insere
      // esses blocos em div.signatures-container ao devolver o termo.
      if (isFirstSign) {
        payload.content_html = this.buildTermDocumentHtml(payload)
      }
      if (isLastSign) {
        payload.send_term = true
      }

      if (this.booking_code || this.bookingRequiredTickets.length > 0) {
        if (this.showSignFooter || this.bookingRequiredTickets.length > 0) {
          try {
            if (this.booking_code) {
              await nodeService.saveTermSignatures(payload)
              this.$emit('sign', payload)
            } else {
              payload.requiredTickets =
                this.bookingRequiredTickets.length > 0 ? true : false

              this.$emit('sign', payload)
            }
          } catch (error) {
            this.$notify({
              message:
                (error.response &&
                  error.response.data &&
                  error.response.data.message) ||
                error.message ||
                'Não foi possível salvar a assinatura do termo.',
              duration: 4000,
              type: 'error',
            })
            this.$emit('close', error)
          }
        }
      } else {
        payload.form = this.form
        this.$emit('sign', payload)
      }
    },

    isClientLocked(client) {
      return Boolean(client && client.signed)
    },

    isClientFormComplete(client) {
      if (!client) return false
      const name = String(client.name || '').trim()
      const birthday = String(client.birthday || '').trim()
      const phone = this.normalizeDoc(client.phone)
      return Boolean(
        this.isValidName(name) &&
          this.isValidDate(birthday) &&
          this.isValidPhone(phone) &&
          this.isValidDoc(client.doc)
      )
    },

    buildTermSignatureClients(datetime) {
      return this.clients
        .filter((client) => !client.signed)
        .map((client) => {
          const signed = this.isClientFormComplete(client)
          const entry = {
            ...client,
            signed,
            datetime: signed ? datetime : client.datetime || null,
          }
          entry.sign_html = signed ? this.buildTermSignaturesHtml(entry) : ''
          return entry
        })
    },

    escapeHtml(value) {
      return String(value == null ? '' : value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')
    },

    /**
     * HTML responsivo do termo (conteúdo de #booking-term-content)
     * pronto para corpo de e-mail ou geração de PDF no servidor.
     */
    buildTermDocumentHtml(payload = {}) {
      const termsHtml = this.getBookingTermContentHtml()
      const signaturesHtml = ''
      const bookingCode = this.escapeHtml(
        payload.booking_code ||
          (this.booking && this.booking.bookingCode) ||
          this.booking_code ||
          ''
      )
      const termHash = this.escapeHtml(payload.hash || '')
      const signedAt = this.escapeHtml(
        moment(payload.datetime || new Date()).format('DD/MM/YYYY HH:mm')
      )

      return `<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <meta http-equiv="X-UA-Compatible" content="IE=edge">
  <title>Termo de Compromisso${bookingCode ? ` — ${bookingCode}` : ''}</title>
  <style>
    /* Reset e tipografia base (e-mail / PDF) */
    body, table, td, p, a, li, blockquote {
      -webkit-text-size-adjust: 100%;
      -ms-text-size-adjust: 100%;
    }
    body {
      margin: 0;
      padding: 0;
      width: 100% !important;
      background: #f4f6f8;
      color: #1a1a1a;
      font-family: Arial, Helvetica, sans-serif;
      line-height: 1.55;
    }
    img { border: 0; outline: none; text-decoration: none; max-width: 100%; height: auto; }
    table { border-collapse: collapse; mso-table-lspace: 0pt; mso-table-rspace: 0pt; }
    .term-outer { width: 100%; background: #f4f6f8; padding: 24px 12px; }
    .term-card {
      width: 100%;
      max-width: 680px;
      margin: 0 auto;
      background: #ffffff;
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      overflow: hidden;
    }
    .term-header {
      padding: 20px 24px;
      background: #0f766e;
      color: #ffffff;
    }
    .term-header h1 {
      margin: 0 0 6px;
      font-size: 20px;
      font-weight: bold;
      line-height: 1.3;
      color: #ffffff;
    }
    .term-header p {
      margin: 0;
      font-size: 13px;
      color: #d1fae5;
    }
    .term-body { padding: 24px; color: #1a1a1a; }
    .term-body h1, .term-body h2, .term-body h3, .term-body h4 {
      color: #111827;
      line-height: 1.35;
      margin: 1.2em 0 0.5em;
    }
    .term-body p { margin: 0 0 0.85em; }
    .term-body ul, .term-body ol { margin: 0 0 1em; padding-left: 1.35em; }
    .term-body li { margin-bottom: 0.35em; }
    .term-body .term-session { margin-bottom: 1.5em; }
    .term-meta {
      width: 100%;
      margin: 0 0 20px;
      font-size: 13px;
      color: #4b5563;
    }
    .term-meta td {
      padding: 6px 0;
      border-bottom: 1px solid #f3f4f6;
      vertical-align: top;
    }
    .term-meta td.label { width: 38%; color: #6b7280; padding-right: 12px; }
    .signatures-title {
      margin: 28px 0 14px;
      padding-top: 18px;
      border-top: 2px solid #e5e7eb;
      font-size: 16px;
      font-weight: bold;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      text-align: center;
      color: #111827;
    }
    .signature-block {
      width: 100%;
      margin: 0 0 16px;
      border: 1px solid #e5e7eb;
      border-radius: 6px;
      overflow: hidden;
    }
    .signature-block th {
      background: #f9fafb;
      text-align: left;
      padding: 10px 14px;
      font-size: 14px;
      color: #111827;
      border-bottom: 1px solid #e5e7eb;
    }
    .signature-block td {
      padding: 8px 14px;
      font-size: 13px;
      vertical-align: top;
      border-bottom: 1px solid #f3f4f6;
    }
    .signature-block tr:last-child td { border-bottom: 0; }
    .signature-block td.label { width: 36%; color: #6b7280; }
    .term-footer {
      padding: 16px 24px 22px;
      font-size: 12px;
      color: #6b7280;
      text-align: center;
      border-top: 1px solid #e5e7eb;
      background: #fafafa;
    }
    @media only screen and (max-width: 620px) {
      .term-outer { padding: 12px 8px !important; }
      .term-body, .term-header, .term-footer { padding-left: 16px !important; padding-right: 16px !important; }
      .term-header h1 { font-size: 18px !important; }
      body { font-size: 14px !important; }
    }
    @media print {
      body, .term-outer { background: #ffffff !important; }
      .term-card { border: 0 !important; max-width: 100% !important; }
    }
  </style>
</head>
<body>
  <div class="term-outer">
    <div class="term-card">
      <div class="term-header">
        <h1>Termo de Compromisso</h1>
        <p>Documento gerado automaticamente para registro da assinatura</p>
      </div>
      <div class="term-body">
        <table class="term-meta" role="presentation" width="100%" cellpadding="0" cellspacing="0">
          ${
            bookingCode
              ? `<tr><td class="label">Código da reserva</td><td><strong>${bookingCode}</strong></td></tr>`
              : ''
          }
          ${
            termHash
              ? `<tr><td class="label">Referência do termo</td><td>${termHash}</td></tr>`
              : ''
          }
          <tr><td class="label">Data/hora da assinatura</td><td>${signedAt}</td></tr>
        </table>
        <div class="term-content">
          ${termsHtml}
        </div>
        <div class="signatures-container">
          ${signaturesHtml}
        </div>
      </div>
      <div class="term-footer">
        Este documento reflete o conteúdo do termo no momento da primeira assinatura.
      </div>
    </div>
  </div>
</body>
</html>`
    },

    getBookingTermContentHtml() {
      const container = document.getElementById('booking-term-content')
      if (container) {
        const clone = container.cloneNode(true)
        // Remove bloco interativo de assinaturas (será recriado em HTML estático)
        const signatures = clone.querySelector('#booking-term-signatures')
        if (signatures) signatures.remove()
        // Remove estados de carregamento e UI auxiliar
        clone
          .querySelectorAll(
            'button, input, select, textarea, .booking-term-collapse-btn'
          )
          .forEach((el) => el.remove())
        const loading = Array.from(clone.querySelectorAll('.text-center')).find(
          (el) => /carregando/i.test(el.textContent || '')
        )
        if (loading) loading.remove()
        return clone.innerHTML.trim() || this.formattedJanooTerms || ''
      }
      return this.formattedJanooTerms || ''
    },

    /**
     * HTML de uma assinatura, para o backend inserir em div.signatures-container.
     * O documento vai completo, sem máscara.
     */
    buildTermSignaturesHtml(client = {}) {
      if (!client) return ''

      const title = [
        client.item_label,
        client.item_label_description
          ? `— ${client.item_label_description}`
          : '',
      ]
        .filter(Boolean)
        .join(' ')
      const personLabel =
        client.person_index && client.item_quantity
          ? `Pessoa ${client.person_index} de ${client.item_quantity}`
          : ''
      const birthday = client.birthday
        ? moment(client.birthday, 'YYYY-MM-DD', true).isValid()
          ? moment(client.birthday).format('DD/MM/YYYY')
          : client.birthday
        : '—'
      const signedAt = client.datetime
        ? moment(client.datetime).format('DD/MM/YYYY HH:mm')
        : '—'

      return `<table class="signature-block" role="presentation" width="100%" cellpadding="0" cellspacing="0">
  <tr><th colspan="2">${this.escapeHtml(title || 'Assinatura')}${
        personLabel
          ? `<div style="font-weight:normal;color:#6b7280;font-size:12px;margin-top:4px;">${this.escapeHtml(
              personLabel
            )}</div>`
          : ''
      }</th></tr>
  <tr><td class="label">Nome</td><td>${this.escapeHtml(
    client.name || '—'
  )}</td></tr>
  <tr><td class="label">Documento</td><td>${this.escapeHtml(
    this.formatDocForSignature(client.doc)
  )}</td></tr>
  <tr><td class="label">Data de nascimento</td><td>${this.escapeHtml(
    birthday
  )}</td></tr>
  <tr><td class="label">Telefone</td><td>${this.escapeHtml(
    client.phone || '—'
  )}</td></tr>
  <tr><td class="label">Assinado em</td><td>${this.escapeHtml(
    signedAt
  )}</td></tr>
</table>`
    },

    formatDocForSignature(doc) {
      const value = String(doc || '').trim()
      if (!value) return '—'
      if (/[A-Za-z]/.test(value)) return value
      return this.formatDoc(this.normalizeDoc(value)) || value
    },

    createEmptyClient(item, personIndex, itemQuantity, expanded = false) {
      return {
        key: `${item.id || item.label || 'item'}-${personIndex}`,
        name: '',
        email: '',
        phone: '',
        doc: '',
        birthday: '',
        signed: false,
        expanded: this.clients.length == 0 ? true : expanded,
        item_id: item.id || '',
        item_label: item.label || item.title || '',
        item_label_description: item.label_description || '',
        person_index: personIndex,
        item_quantity: itemQuantity,
        device: null,
        location: null,
      }
    },

    toggleClientGroup(index) {
      const client = this.clients[index]
      if (!client) return
      this.$set(client, 'expanded', !client.expanded)
    },

    buildClientsFromItems() {
      let items =
        this.items && this.items.length
          ? this.items
          : this.booking && this.booking.payload && this.booking.payload.items
          ? this.booking.payload.items
          : []
      items = items.filter((item) => Number(item.quantity) > 0)
      const clients = []
      const vm = this
      let signOnLoad = false
      items.forEach((item, index) => {
        const quantity = Number(item.quantity) || 0
        for (let personIndex = 1; personIndex <= quantity; personIndex++) {
          if (vm.bookingRequiredTickets.length > 0 || vm.showSignFooter) {
            if (vm.bookingRequiredTickets.find((t) => t.id == item.id)) {
              clients.push(
                this.createEmptyClient(item, personIndex, quantity, true)
              )
              signOnLoad = true
            } else if (this.booking_code) {
              clients.push(this.createEmptyClient(item, personIndex, quantity))
            }
          } else if (index == 0 && personIndex == 1) {
            clients.push(this.createEmptyClient(item, personIndex, quantity))
          }
        }
      })
      this.clients = clients
    },

    mergeSignedClients(signedClients) {
      if (!Array.isArray(signedClients) || !signedClients.length) return
      signedClients.forEach((signedClient, signedIndex) => {
        let targetIndex = this.clients.findIndex((client) => {
          if (
            signedClient.doc &&
            this.normalizeDoc(client.doc) ===
              this.normalizeDoc(signedClient.doc)
          ) {
            return true
          }
          return (
            client.item_id === signedClient.item_id &&
            client.person_index === signedClient.person_index
          )
        })

        if (targetIndex < 0 && signedIndex < this.clients.length) {
          targetIndex = signedIndex
        }

        if (targetIndex < 0) return

        const current = this.clients[targetIndex]

        this.$set(this.clients, targetIndex, {
          ...current,
          ...signedClient,
          key: current.key,
          expanded: this.hasUnsignedClients ? true : false,
          signed: signedClient.signed !== false,
          item_label: signedClient.item_label || current.item_label,
          item_label_description:
            signedClient.item_label_description ||
            current.item_label_description,
          person_index: signedClient.person_index || current.person_index,
          item_quantity: signedClient.item_quantity || current.item_quantity,
        })
      })
    },

    normalizeDoc(doc) {
      return String(doc || '').replace(/\D/g, '')
    },

    isValidDate(date) {
      if (!date) return false
      return moment(date, 'YYYY-MM-DD', true).isValid()
    },

    isValidPhone(phone) {
      return this.normalizeDoc(phone).length >= 10
    },

    isValidName(name) {
      name = name.trim()
      if (!name) return false
      const parts = name.split(' ')
      if (parts.length < 2) return false
      if (name.replaceAll(' ', '').length < 5) return false
      return true
    },
    isValidCnpj(doc) {
      if (doc.length !== 14) return false
      if (/^(\d)\1+$/.test(doc)) return false

      const calcDigit = (base, weights) => {
        let sum = 0
        for (let i = 0; i < weights.length; i++) {
          sum += Number(base[i]) * weights[i]
        }
        const rest = sum % 11
        return rest < 2 ? 0 : 11 - rest
      }

      const digit1 = calcDigit(doc, [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2])
      if (digit1 !== Number(doc[12])) return false

      const digit2 = calcDigit(doc, [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2])
      return digit2 === Number(doc[13])
    },
    isValidPassport(doc) {
      const passport = String(doc || '')
        .trim()
        .toUpperCase()
      if (passport.length !== 8) return false
      return /^[A-Z]{2}[0-9]{6}$/.test(passport)
    },
    isValidDoc(doc) {
      const raw = String(doc || '')
        .trim()
        .toUpperCase()
      if (this.isValidPassport(raw)) return true

      const normalized = this.normalizeDoc(raw)
      if (normalized.length == 14) {
        return this.isValidCnpj(normalized)
      }
      if (normalized.length !== 11) {
        return false
      }
      if (/^(\d)\1+$/.test(normalized)) {
        return false
      }

      let sum = 0
      for (let i = 0; i < 9; i++) sum += Number(normalized[i]) * (10 - i)
      let rest = (sum * 10) % 11
      if (rest === 10) rest = 0
      if (rest !== Number(normalized[9])) return false

      sum = 0
      for (let i = 0; i < 10; i++) sum += Number(normalized[i]) * (11 - i)
      rest = (sum * 10) % 11
      if (rest === 10) rest = 0
      return rest === Number(normalized[10])
    },

    getDocMask(client) {
      if (this.isClientLocked(client)) return ''

      const value = String((client && client.doc) || '')
      if (/[A-Za-z]/.test(value)) return 'AA######'

      const digits = this.normalizeDoc(value)
      if (digits.length > 11) return '##.###.###/####-##'
      return '###.###.###-##'
    },

    formatDoc(doc) {
      if (doc.length > 11) return this.formatCnpj(doc)
      return this.formatCpf(doc)
    },
    formatCpf(doc, obfuscate = false) {
      const cpf = this.normalizeDoc(doc)
      if (cpf.length !== 11) return doc || ''
      if (obfuscate)
        return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.***.***-$4')
      return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
    },

    abbreviateName(name) {
      const parts = String(name || '')
        .trim()
        .split(/\s+/)
        .filter(Boolean)
      if (!parts.length) return '—'
      if (parts.length === 1) return parts[0]
      const lastName = parts[parts.length - 1]
      const initials = parts
        .slice(1)
        .map((part) => `${part.charAt(0).toUpperCase()}.`)
        .join(' ')

      return `${initials} ${lastName}`.trim()
    },

    formatSignatureDate(client) {
      const raw =
        (client && (client.signed_at || client.datetime)) ||
        (client && client.device && client.device.timestamp) ||
        this.termDate ||
        (this.booking &&
          this.booking.payload &&
          this.booking.payload.term &&
          (this.booking.payload.term.date ||
            this.booking.payload.term.datetime))

      if (!raw) return '—'

      const parsed = moment(raw)
      return parsed.isValid() ? parsed.format('DD/MM/YYYY HH:mm') : String(raw)
    },

    async loadTermSignaturesForCard() {
      const hash =
        (this.booking &&
          this.booking.payload &&
          this.booking.payload.hash &&
          this.booking.payload.hash) ||
        this.hash ||
        this.term_hash

      if (!hash) return

      this.term_hash = hash
      if (this.booking_code) {
        try {
          const response = await nodeService.getTermSignatures(hash)

          const result = response.data || response
          this.clients = (result.clients || [])
            .filter((client) => client.signed)
            .map((client, index) => ({
              ...client,
              key: client.key || `${client.doc || 'client'}-${index}`,
              signed: true,
            }))

          let signedClients = this.clients.filter((client) => client.signed)
        } catch (error) {
          this.clients = []
        }
      }
    },

    async initCardMode() {
      this.loadingCards = true

      if (this.booking_code) {
        await this.fetchBookingByUUID(this.booking_code)
        this.booking = this.bookingDetails[0] || null
      }

      this.term_hash =
        this.hash ||
        (this.booking &&
          this.booking.payload &&
          this.booking.payload.term &&
          this.booking.payload.term.hash) ||
        ''

      await this.loadTermSignaturesForCard()
      this.loadingCards = false
    },

    formatCnpj(doc) {
      const cnpj = this.normalizeDoc(doc)
      if (cnpj.length !== 14) return doc || ''
      return cnpj.replace(
        /(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
        '$1.$2.$3/$4-$5'
      )
    },

    formatCustomerPhone(phone) {
      if (!phone) return ''
      if (typeof phone === 'string') return phone

      const area = phone.area_code || ''
      const number = phone.number || ''
      const digits = this.normalizeDoc(`${area}${number}`)

      if (digits.length === 11) {
        return digits.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3')
      }
      if (digits.length === 10) {
        return digits.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3')
      }

      return [area, number].filter(Boolean).join(' ')
    },

    hasSignatureWithDoc(doc) {
      const normalized = this.formatCpf(doc, true)
      if (!normalized) return false
      return this.clients.some((client) => {
        return client.signed && client.doc === normalized
      })
    },

    prefillCustomerAsClient() {
      const customer = this.customer
      if (!customer) return

      const customerDoc = customer.cpf || customer.doc || ''
      if (this.hasSignatureWithDoc(customerDoc)) return

      const targetIndex = this.clients.findIndex(
        (client) => !this.isClientLocked(client) && !client.doc && !client.name
      )
      if (targetIndex < 0) return

      const current = this.clients[targetIndex]
      this.$set(this.clients, targetIndex, {
        ...current,
        name: customer.name || '',
        email: customer.email || '',
        phone: this.formatCustomerPhone(customer.phone),
        doc: this.formatCpf(customerDoc),
        birthday: customer.birthday || customer.birthdate || '',
        signed: false,
      })
    },

    async loadTermSignatures() {
      const hash =
        (this.booking &&
          this.booking.payload &&
          this.booking.payload.hash &&
          this.booking.payload.hash) ||
        this.hash ||
        this.term_hash

      if (!hash) return

      this.term_hash = hash
      let signedClients = []
      try {
        if (this.booking_code) {
          const response = await nodeService.getTermSignatures(hash)

          const result = response.data || response
          signedClients = result.clients || []
          signedClients = signedClients.filter((client) => client.signed)

          if (
            signedClients.length > 0 &&
            signedClients.length == this.clients.length
          ) {
            this.$notify({
              message: 'Termo assinado!',
              duration: 4000,
              type: 'success',
            })
          } else {
            this.$notify({
              message: 'assinaturas encontradas: ' + signedClients.length,
              duration: 4000,
              type: 'success',
            })
            setTimeout(() => {
              this.$notify({
                message:
                  'assinaturas pendentes: ' +
                  (this.clients.length - signedClients.length),
                duration: 4000,
                type: 'warning',
              })
            }, 1000)
          }
        }
      } catch (error) {
        if (this.booking_code && !this.isQuotationBooking) {
          this.$notify({
            message:
              (error.response &&
                error.response.data &&
                error.response.data.message) ||
              error.message ||
              'Nenhuma assinatura encontrada.',
            duration: 4000,
            type: 'warning',
          })
          this.signaturesLoaded = true
        }
      } finally {
        this.loading = false
      }
      this.mergeSignedClients(signedClients)
    },

    buildDeviceContext() {
      const nav = typeof navigator !== 'undefined' ? navigator : {}
      const screenObj = typeof window !== 'undefined' ? window.screen : {}

      return {
        id: uuidv4(),
        userAgent: nav.userAgent || '',
        platform: nav.platform || '',
        language: nav.language || '',
        languages: Array.isArray(nav.languages) ? [...nav.languages] : [],
        vendor: nav.vendor || '',
        cookieEnabled: Boolean(nav.cookieEnabled),
        hardwareConcurrency: nav.hardwareConcurrency || null,
        maxTouchPoints: nav.maxTouchPoints || 0,
        screen: {
          width: screenObj.width || null,
          height: screenObj.height || null,
          availWidth: screenObj.availWidth || null,
          availHeight: screenObj.availHeight || null,
          colorDepth: screenObj.colorDepth || null,
        },
        timezone:
          (Intl &&
            Intl.DateTimeFormat &&
            Intl.DateTimeFormat().resolvedOptions().timeZone) ||
          '',
        timestamp: new Date().toISOString(),
      }
    },

    requestDeviceLocation() {
      return new Promise((resolve) => {
        if (
          typeof navigator === 'undefined' ||
          !navigator.geolocation ||
          typeof navigator.geolocation.getCurrentPosition !== 'function'
        ) {
          resolve({
            granted: false,
            denied: true,
            latitude: null,
            longitude: null,
            accuracy: null,
            error: 'geolocation_unavailable',
          })
          return
        }

        navigator.geolocation.getCurrentPosition(
          (position) => {
            resolve({
              granted: true,
              denied: false,
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              accuracy: position.coords.accuracy,
              error: null,
            })
          },
          (error) => {
            resolve({
              granted: false,
              denied: true,
              latitude: null,
              longitude: null,
              accuracy: null,
              error:
                (error && error.message) ||
                'localizacao_nao_liberada_pelo_usuario',
            })
          },
          {
            enableHighAccuracy: false,
            timeout: 10000,
            maximumAge: 60000,
          }
        )
      })
    },

    async collectSignatureContext() {
      this.deviceContext = this.buildDeviceContext()
      this.locationContext = await this.requestDeviceLocation()
    },

    attachSignatureContextToClients() {
      this.clients.forEach((client, index) => {
        if (this.isClientLocked(client) && client.device && client.location) {
          return
        }

        this.$set(this.clients, index, {
          ...client,
          device: this.deviceContext,
          location: this.locationContext,
        })
      })
    },
  },
  async mounted() {
    if (!this.booking_code) {
      await this.resetBookingDetails()
    }
    if (this.isCardMode) {
      await this.initCardMode()
      return
    }

    if (this.booking_code) {
      try {
        await this.fetchBookingByUUID(this.booking_code)
      } catch (error) {
        this.booking = null
      }
      this.booking = this.bookingDetails[0] || null
      if (!this.booking || !this.booking.payload) {
        swal.fire({
          title: 'Código de reserva inválido',
          text:
            'Não foi possível encontrar a reserva: #' +
            (this.booking_code || ''),
          icon: 'error',
        })
        return
      }
      this.customer = this.booking.payload.costumer || {}
      this.placeUuid =
        this.booking.payload.product && this.booking.payload.product.place_uuid
      this.productUuid =
        this.booking.payload.product && this.booking.payload.product.uuid
      await this.loadPois()
      this.term_hash =
        this.hash ||
        (this.booking.payload.term && this.booking.payload.term.hash) ||
        ''
    } else if (this.form) {
      this.customer = {
        name: this.form.name,
        email: this.form.email,
        phone: this.form.phone,
        doc: this.form.cpf_cnpj,
        birthday: '',
        signed: false,
      }
      this.placeUuid = this.place_uuid
      this.productUuid = this.product_uuid
      await this.loadPois()
      this.term_hash = this.form.hash
    } else {
      swal.fire({
        title: 'Dados insuficientes para assinar o termo',
        text: 'Não foi possível encontrar as informações da reserva',
        icon: 'error',
      })
      return
    }

    await this.loadTerms()
    this.showSignFooter =
      this.$route.params.term_hash == this.term_hash || !this.booking_code

    this.buildClientsFromItems()
    await this.loadTermSignatures()
    this.prefillCustomerAsClient()
    if (this.bookingRequiredTickets.length > 0) {
      this.sign()
    }

    if (this.hasUnsignedClients) {
      await this.collectSignatureContext()
      this.attachSignatureContextToClients()
    }
  },
}
</script>
<style>
/*
#term-session-0 {
  background-color: #0000ff1c;
}

#term-session-1 {
  background-color: #00fa4a1c;
}

#term-session-2 {
  background-color: #fc00761c;
}

#term-session-3 {
  background-color: #fce0001c;
}
*/
.term-session {
  border-right: 1px solid #0c74b266;
  padding-right: 2px;
}
</style>
<style scoped>
#sign-button:disabled {
  display: none;
}
#scroll-button:disabled {
  display: none;
}
.is-invalid {
  border: 1px solid #dc3545 !important;
}

.booking-term {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.booking-term-header {
  flex-shrink: 0;
}

.booking-term-footer {
  margin-top: 2dvh !important;
  margin-bottom: 0;
  padding-top: 1dvh !important;
  padding-bottom: 0 !important;
}

.booking-term-content {
  height: 83vh;
  height: 83dvh;
  max-height: 83vh;
  max-height: 83dvh;
  flex: 0 0 auto;
  min-height: 0;
  overflow-y: auto;
  padding-right: 0.25rem;
}

.booking-term-client-group {
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  padding: 12px;
  background: #fafafa;
}

.booking-term-client-header {
  cursor: pointer;
  user-select: none;
}

.booking-term-collapse-btn {
  color: #12576e;
  font-weight: 700;
  font-size: 1rem;
  line-height: 1;
  text-decoration: none !important;
  min-width: 1.5rem;
  text-align: center;
}

.booking-term-footer {
  flex-shrink: 0;
  background: #fff;
  z-index: 2;
}

.booking-term--card {
  display: block;
}

.booking-term-cards {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.booking-term-signature-card {
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  padding: 10px 12px;
  background: #fafafa;
}

.booking-term-signature-card-name {
  font-weight: 700;
  color: #27272c;
  font-size: 14px;
}

.booking-term-signature-card-doc {
  color: #3d3d3d;
  font-size: 13px;
  margin-top: 2px;
}

.booking-term-signature-card-date {
  color: #8898aa;
  font-size: 12px;
  margin-top: 4px;
}
</style>
